<template>
  <div class="row">
    <div class="col-12 text-center">
      {{ getDateFormatted() }}
    </div>
    <nav aria-label="Monatsnavigation">
      <ul class="pagination justify-content-between">
        <li class="page-item">
          <a class="page-link" @click="prevMonth">&laquo;</a>
        </li>
        <li class="page-item">
          <a class="page-link" @click="nextMonth">&raquo;</a>
        </li>
      </ul>
    </nav>
  </div>

  <div v-if="loading" class="text-center py-5">
    <div class="spinner-border text-primary" role="status">
      <span class="visually-hidden">Laden...</span>
    </div>
  </div>

  <template v-else>
    <div v-if="entries.length === 0" class="text-center text-muted py-5">
      {{ $t('payment_overview_no_entries', { date: getDateFormatted() }) }}
    </div>

    <template v-else>
      <!-- Summary KPI Cards -->
      <div class="row g-3 mb-4">
        <div class="col-12 col-sm-4">
          <div class="card text-center shadow-sm h-100">
            <div class="card-body py-3">
              <div class="fw-bold fs-5 text-primary">{{ formatCost(total) }} €</div>
              <div class="text-muted small">{{ $t('payment_overview_total') }}</div>
            </div>
          </div>
        </div>
        <div class="col-12 col-sm-4">
          <div class="card text-center shadow-sm h-100">
            <div class="card-body py-3">
              <div class="fw-bold fs-5">{{ entries.length }}</div>
              <div class="text-muted small">{{ $t('payment_overview_entries') }}</div>
            </div>
          </div>
        </div>
        <div class="col-12 col-sm-4">
          <div class="card text-center shadow-sm h-100">
            <div class="card-body py-3">
              <div class="fw-bold fs-5">{{ groupedEntries.length }}</div>
              <div class="text-muted small">{{ $t('payment_overview_organisations') }}</div>
            </div>
          </div>
        </div>
      </div>

      <!-- Desktop -->
      <div class="d-none d-md-block">
        <div class="card shadow-sm">
          <div class="card-body p-0">
            <div class="table-responsive">
              <table class="table table-hover table-bordered mb-0">
                <thead class="table-light">
                  <tr>
                    <th>{{ $t('payment_overview_organisation') }}</th>
                    <th>{{ $t('payment_overview_plant') }}</th>
                    <th class="text-center">{{ $t('payment_overview_billing_month') }}</th>
                    <th class="text-end">{{ $t('payment_overview_amount') }}</th>
                  </tr>
                </thead>
                <tbody>
                  <template v-for="group in groupedEntries" :key="group.organisation">
                    <tr v-for="(entry, idx) in group.items" :key="idx">
                      <td>{{ idx === 0 ? entry.organisation : '' }}</td>
                      <td>
                        {{ entry.name }}
                        <i class="fa-solid fa-bench-tree ms-1 text-muted" v-if="entry.is_park"></i>
                      </td>
                      <td class="text-center">{{ formatBillingMonth(entry) }}</td>
                      <td class="text-end">{{ formatCost(entry.monthly_cost) }} €</td>
                    </tr>
                    <tr class="table-secondary">
                      <td colspan="3" class="text-end fst-italic text-muted small">
                        {{ $t('payment_overview_subtotal') }} {{ group.organisation }}
                      </td>
                      <td class="text-end fw-semibold">{{ formatCost(group.subtotal) }} €</td>
                    </tr>
                  </template>
                </tbody>
                <tfoot class="table-light">
                  <tr>
                    <th colspan="3" class="text-end">{{ $t('payment_overview_total') }}</th>
                    <th class="text-end">{{ formatCost(total) }} €</th>
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>
        </div>
      </div>

      <!-- Mobile -->
      <div class="d-md-none">
        <template v-for="group in groupedEntries" :key="group.organisation">
          <div class="ios-payment-card mb-2">
            <div class="ios-row ios-row--header">
              <span class="ios-row__title">{{ group.organisation }}</span>
              <span class="ios-row__value fw-semibold">{{ formatCost(group.subtotal) }} €</span>
            </div>
            <template v-for="(entry, idx) in group.items" :key="idx">
              <div class="ios-row">
                <span class="ios-row__label">
                  {{ entry.name }}
                  <i class="fa-solid fa-bench-tree ms-1 text-muted" v-if="entry.is_park"></i>
                </span>
              </div>
              <div class="ios-row">
                <span class="ios-row__label">{{ $t('payment_overview_billing_month') }}</span>
                <span class="ios-row__value">{{ formatBillingMonth(entry) }}</span>
              </div>
              <div :class="['ios-row', idx === group.items.length - 1 ? 'ios-row--last' : '']">
                <span class="ios-row__label">{{ $t('payment_overview_amount') }}</span>
                <span class="ios-row__value fw-semibold"
                  >{{ formatCost(entry.monthly_cost) }} €</span
                >
              </div>
            </template>
          </div>
        </template>

        <div class="ios-payment-card">
          <div class="ios-row ios-row--last d-flex justify-content-between">
            <span class="fw-semibold">{{ $t('payment_overview_total') }}</span>
            <span class="fw-bold">{{ formatCost(total) }} €</span>
          </div>
        </div>
      </div>
    </template>
  </template>
</template>

<script>
import axios from 'axios';

export default {
  name: 'PaymentOverviewParks',
  data() {
    return {
      currentDate: new Date(),
      entries: [],
      total: 0,
      loading: false,
    };
  },
  computed: {
    groupedEntries() {
      const map = {};
      for (const entry of this.entries) {
        if (!map[entry.organisation]) {
          map[entry.organisation] = { organisation: entry.organisation, items: [], subtotal: 0 };
        }
        map[entry.organisation].items.push(entry);
        map[entry.organisation].subtotal += parseFloat(entry.monthly_cost);
      }
      return Object.values(map);
    },
  },
  mounted() {
    this.loadData();
  },
  methods: {
    loadData() {
      this.loading = true;
      const month = this.currentDate.getMonth() + 1;
      const year = this.currentDate.getFullYear();
      axios
        .get(`/api/v1/billing/payment_overview/?month=${month}&year=${year}`)
        .then((response) => {
          this.entries = response.data.entries;
          this.total = response.data.total;
        })
        .catch((err) => {
          console.error('Failed to load payment overview:', err);
          this.entries = [];
          this.total = 0;
        })
        .finally(() => {
          this.loading = false;
        });
    },
    prevMonth() {
      this.currentDate = new Date(
        this.currentDate.getFullYear(),
        this.currentDate.getMonth() - 1,
        1
      );
      this.loadData();
    },
    nextMonth() {
      this.currentDate = new Date(
        this.currentDate.getFullYear(),
        this.currentDate.getMonth() + 1,
        1
      );
      this.loadData();
    },
    getDateFormatted() {
      return this.currentDate.toLocaleDateString('de-DE', {
        month: '2-digit',
        year: 'numeric',
      });
    },
    formatBillingMonth(entry) {
      const date = new Date(entry.billing_year, entry.billing_month - 1, 1);
      return date.toLocaleDateString('de-DE', { month: '2-digit', year: 'numeric' });
    },
    formatCost(value) {
      return parseFloat(value).toFixed(2);
    },
  },
};
</script>

<style scoped>
@media (max-width: 767.98px) {
  .ios-payment-card {
    background: #ffffff;
    border-radius: 12px;
    overflow: hidden;
    box-shadow:
      0 1px 3px rgba(0, 0, 0, 0.08),
      0 0 0 0.5px rgba(0, 0, 0, 0.06);
  }

  .ios-row {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 11px 16px;
    min-height: 44px;
    background: #ffffff;
    border-bottom: 0.5px solid rgba(60, 60, 67, 0.18);
  }

  .ios-row--header {
    background: #f2f2f7;
    font-weight: 600;
    font-size: 0.9375rem;
  }

  .ios-row--last {
    border-bottom: none;
  }

  .ios-row__label {
    font-size: 0.9375rem;
    color: #1c1c1e;
  }

  .ios-row__title {
    font-size: 0.9375rem;
    color: #1c1c1e;
    font-weight: 600;
  }

  .ios-row__value {
    font-size: 0.9375rem;
    color: #3a3a3c;
  }
}
</style>
